import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/**
 * Editor rich-text profissional (Quill) para parecer/minuta do Processo Administrativo.
 */
const FONT_OPTIONS = [
  { value: 'arial', label: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { value: 'times-new-roman', label: 'Times New Roman', css: '"Times New Roman", Times, serif' },
  { value: 'georgia', label: 'Georgia', css: 'Georgia, serif' },
  { value: 'calibri', label: 'Calibri', css: 'Calibri, Candara, sans-serif' },
  { value: 'verdana', label: 'Verdana', css: 'Verdana, Geneva, sans-serif' },
  { value: 'tahoma', label: 'Tahoma', css: 'Tahoma, Geneva, sans-serif' },
  { value: 'courier-new', label: 'Courier New', css: '"Courier New", Courier, monospace' },
];

const FONT_VALUES = FONT_OPTIONS.map((f) => f.value);

const QUILL_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'script',
  'list',
  'bullet',
  'indent',
  'align',
  'blockquote',
  'link',
  'clean',
];

const FONT_STYLE_ID = 'pa-quill-font-styles';

function ensureFontStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FONT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = FONT_STYLE_ID;
  style.textContent = `
    .ql-snow .ql-picker.ql-font .ql-picker-label::before { content: "Fonte"; }
    .ql-snow .ql-picker.ql-size .ql-picker-label::before { content: "Tamanho"; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="small"]::before { content: "Pequeno"; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="large"]::before { content: "Grande"; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="huge"]::before { content: "Enorme"; }
    ${FONT_OPTIONS.map(
      (f) => `
      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${f.value}"]::before,
      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${f.value}"]::before {
        content: "${f.label}";
        font-family: ${f.css};
      }
      .ql-font-${f.value} { font-family: ${f.css}; }
    `
    ).join('\n')}
  `;
  document.head.appendChild(style);
}

function registerQuillFonts() {
  const Quill = ReactQuill.Quill;
  if (!Quill || Quill.__paFontsRegistered) return;
  const Font = Quill.import('formats/font');
  Font.whitelist = FONT_VALUES;
  Quill.register(Font, true);
  const Size = Quill.import('formats/size');
  Size.whitelist = ['small', false, 'large', 'huge'];
  Quill.register(Size, true);
  Quill.__paFontsRegistered = true;
}

registerQuillFonts();
ensureFontStyles();

export default function PaRichTextEditor({
  value,
  onChange,
  minHeight = 320,
  placeholder = 'Digite o conteúdo…',
  readOnly = false,
}) {
  const modules = useMemo(
    () => ({
      toolbar: readOnly
        ? false
        : [
            [{ font: FONT_VALUES }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            ['blockquote', 'link'],
            ['clean'],
          ],
      clipboard: { matchVisual: false },
    }),
    [readOnly]
  );

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(25,118,210,0.18)',
        bgcolor: '#fff',
        '& .ql-toolbar.ql-snow': {
          border: 'none',
          borderBottom: '1px solid rgba(25,118,210,0.12)',
          bgcolor: 'rgba(25,118,210,0.04)',
          flexWrap: 'wrap',
        },
        '& .ql-container.ql-snow': {
          border: 'none',
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: 14,
          minHeight,
        },
        '& .ql-editor': {
          minHeight,
          lineHeight: 1.55,
          color: '#1a1a1a',
        },
        '& .ql-editor.ql-blank::before': {
          fontStyle: 'normal',
          color: '#9e9e9e',
        },
      }}
    >
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={(html) => onChange?.(html)}
        modules={modules}
        formats={QUILL_FORMATS}
        readOnly={readOnly}
        placeholder={placeholder}
      />
    </Box>
  );
}
